import { CommonModule } from '@angular/common';
import { Component, Input, inject, OnChanges } from '@angular/core';
import { StyleDefinition } from '@angular/flex-layout';
import { RouterModule } from '@angular/router';

import {
  CmsImage,
  CmsUrl,
  Image,
  ImageLoadingMethod,
  ImageSizeStyle,
} from '@digital-platform/rs-cms/domain';
import { ImageLoading } from '@digital-platform/rs-shared/domain';
import {
  DESKTOP,
  MIDSIZE,
  MOBILE,
  ViewportService,
} from '@digital-platform/rs-shared/services';
import {
  AsNgStylePipe,
  EncodePathPipe,
  SafeHtmlPipe,
} from '@digital-platform/rs-shared/ui';
import { BasePTypeComponent } from '../base-ptype/base-ptype.component';

interface ImageAngularConfig {
  mobileHeight?: number;
  midsizeHeight?: number;
  desktopHeight?: number;
  mobileWidth?: number;
  midsizeWidth?: number;
  desktopWidth?: number;
  class?: string;
  mobileClass?: string;
  midsizeClass?: string;
  desktopClass?: string;
}

let imageId = 0;

@Component({
  selector: 'digital-platform-image',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    AsNgStylePipe,
    SafeHtmlPipe,
    EncodePathPipe,
  ],
  providers: [AsNgStylePipe, EncodePathPipe],
  templateUrl: './image.component.html',
  styleUrls: ['./image.component.scss'],
})
export class ImageComponent
  extends BasePTypeComponent
  implements Image, OnChanges
{
  @Input() public imageOverride: Image | undefined;

  public override type = 'image' as const;

  public viewportService = inject(ViewportService);
  private asNgStyle = inject(AsNgStylePipe);

  public emptyCmsImage = {
    target_id: null,
    image_use: null,
    alt: null,
    title: null,
    width: null,
    height: null,
    src: '',
  };

  public imageId = 'image-' + imageId++;
  public mq = { mobile: MOBILE, midsize: MIDSIZE, desktop: DESKTOP };
  public defaultImageLoading = {
    tid: 5943,
    name: 'Lazy',
    value: ImageLoading.enum.lazy,
  };

  @Input() public analyticsLinkText = '';
  @Input() public imageHeight = '';
  @Input() public loading: ImageLoadingMethod = this.defaultImageLoading;
  @Input() public imageSize: ImageSizeStyle | null = {};
  @Input() public imageSizeMobile: ImageSizeStyle | null = {};
  @Input() public imageWidth = '';
  @Input() public url: CmsUrl = [];
  @Input() public webpMedia: CmsImage | null | undefined = null;
  @Input() public webpMediaMid: CmsImage | null | undefined = null;
  @Input() public webpMediaDesktop: CmsImage | null | undefined = null;
  @Input() public class: string | null = '';
  @Input() public angularConfig?: ImageAngularConfig;

  public imgStyleDesktop: StyleDefinition = {};
  public imgStyleMobile: StyleDefinition = {};
  public imgStyleMid: StyleDefinition = {};
  public classNames = '';

  ngOnChanges() {
    if (this.imageOverride) {
      // Override all values coming from a single object.
      Object.assign(this, this.imageOverride);
      if (this.angularConfig && typeof this.angularConfig === 'string') {
        try {
          this.angularConfig = JSON.parse(this.angularConfig);
        } catch (e) {
          console.error(`Unable to parse ${this.angularConfig} as JSON.`);
        }
      }
    }

    this.initStyles();
    this.initClasses();
  }

  private initClasses() {
    const cls = [];

    if (this.class) cls.push(this.class);

    if (this.angularConfig) {
      const {
        class: c,
        mobileClass,
        midsizeClass,
        desktopClass,
      } = this.angularConfig;
      if (this.viewportService.isMobile) {
        if (mobileClass) cls.push(mobileClass);
      } else if (this.viewportService.isMidsize) {
        if (midsizeClass) cls.push(midsizeClass);
      } else if (this.viewportService.isDesktop) {
        if (desktopClass) cls.push(desktopClass);
      }

      if (c) cls.push(c);
    }

    this.classNames = cls.join(' ');
  }

  private initStyles() {
    const mobile: StyleDefinition = {},
      desktop: StyleDefinition = {},
      midsize: StyleDefinition = {};
    mobile['width'] = `${this.mobileWebpMedia.width}px`;
    mobile['height'] = `${this.mobileWebpMedia.height}px`;
    midsize['width'] = `${this.midWebpMedia.width}px`;
    midsize['height'] = `${this.midWebpMedia.height}px`;
    desktop['width'] = `${this.desktopWebpMedia.width}px`;
    desktop['height'] = `${this.desktopWebpMedia.height}px`;
    if (this.angularConfig) {
      const {
        mobileHeight,
        midsizeHeight,
        desktopHeight,
        midsizeWidth,
        mobileWidth,
        desktopWidth,
      } = this.angularConfig;
      if (mobileHeight) mobile['height'] = `${mobileHeight}px`;
      if (mobileWidth) mobile['width'] = `${mobileWidth}px`;
      if (midsizeHeight) midsize['height'] = `${midsizeHeight}px`;
      if (midsizeWidth) midsize['width'] = `${midsizeWidth}px`;
      if (desktopHeight) desktop['height'] = `${desktopHeight}px`;
      if (desktopWidth) desktop['width'] = `${desktopWidth}px`;
    }
    if (this.imageWidth) {
      mobile['width'] =
        midsize['width'] =
        desktop['width'] =
          `${this.imageWidth}px`;
    }
    if (this.imageWidth) {
      mobile['height'] =
        midsize['height'] =
        desktop['height'] =
          `${this.imageHeight}px`;
    }
    this.imgStyleMobile = mobile;
    this.imgStyleMid = midsize;
    this.imgStyleDesktop = desktop;
  }

  get mediaQueryStyles() {
    const selector = `img#${this.imageId}`;
    const mobile = this.asNgStyle.transform(this.imgStyleMobile);
    const midsize = this.asNgStyle.transform(this.imgStyleMid);
    const desktop = this.asNgStyle.transform(this.imgStyleDesktop);
    return `
      <style>
      @media ${MOBILE} {${selector}{${mobile}}}
      @media ${MIDSIZE} {${selector}{${midsize}}}
      @media ${DESKTOP} {${selector}{${desktop}}}
      </style>
    `;
  }

  get mobileWebpMedia() {
    return (
      this.webpMedia ||
      this.webpMediaMid ||
      this.webpMediaDesktop ||
      this.emptyCmsImage
    );
  }

  get midWebpMedia() {
    return (
      this.webpMediaMid ||
      this.webpMedia ||
      this.webpMediaDesktop ||
      this.emptyCmsImage
    );
  }

  get desktopWebpMedia() {
    return (
      this.webpMediaDesktop ||
      this.webpMediaMid ||
      this.webpMedia ||
      this.emptyCmsImage
    );
  }

  get fallbackWebpMedia(): CmsImage {
    const webpMedia = this.desktopWebpMedia;
    const src = String(webpMedia.src)
      .replace(/\/styles\/[^/]+\/public\//, '/')
      .replace(/\.webp$/, '');
    return { ...webpMedia, src };
  }

  getMediaType(src?: string | null): string | null {
    if (/\.webp$/i.test(src as string)) {
      return 'image/webp';
    } else if (/\.png$/i.test(src as string)) {
      return 'image/png';
    } else if (/\.jpe?g$/i.test(src as string)) {
      return 'image/jpeg';
    } else if (src) {
      return 'image/' + src.split('.').pop();
    }
    return null;
  }
}
